
import dayjs from 'dayjs'
import { useState } from 'react'
import { Controller, SubmitHandler, useForm } from 'react-hook-form'

import { updateBreakDuration } from '@/entities/gridTables/thunks/updateBreakDuration'
import { updatePlanningShiftTableState } from '@/entities/gridTables/thunks/updatePlanningShiftTableState'
import { fetchScenarios } from '@/entities/schedules'
import { Button, Modal } from '@/shared/components'
import { StyledForm } from '@/shared/components/LoginCard/StyledLogin'
import { DialogComponentProps } from '@/shared/components/Modal/models/DialogStructure'
import { TimePicker } from '@/shared/components/TimePicker/TimePicker'
import { timeDayjsToHhMmSs, timeHhMmSsToDayjs } from '@/shared/lib/timeHelper'
import { useAppDispatch } from '@/shared/store'
import { styled } from '@mui/material'

export type EditBreakDurationProps = DialogComponentProps & {
  onClose: () => void
}

type EditBreakDurationFormValues = {
  breakDuration: dayjs.Dayjs
}

export const EditBreakDuration: React.FC<EditBreakDurationProps> = ({
  open,
  onClose,
  breakDuration,
  stopId,
  scenarioId,
  shiftId,
}) => {
  const [isLoading, setLoading] = useState(false)
  const dispatch = useAppDispatch()
  const { control, handleSubmit } = useForm<EditBreakDurationFormValues>({
    defaultValues: {
      breakDuration: timeHhMmSsToDayjs(breakDuration) || dayjs(),
    },
  })

  const onSubmit: SubmitHandler<EditBreakDurationFormValues> = async (data) => {
    setLoading(true)

    const breakDurationString = timeDayjsToHhMmSs(data.breakDuration)

    if (!breakDurationString) return

    await dispatch(updateBreakDuration({
      stopId: stopId,
      breakDuration: breakDurationString,
    }))

    await dispatch(fetchScenarios({ scenarioIds: [scenarioId] }))
    await dispatch(updatePlanningShiftTableState({ shiftId: shiftId }))

    onClose()
    setLoading(false)
  }

  return (
    <Modal
      title={'Update Break Duration'}
      onClose={onClose}
      open={open}
      transition="opacity"
      showBackground={true}
    >
      <StyledContent>
        <StyledForm onSubmit={handleSubmit(onSubmit)} noValidate autoComplete="off">
          <Controller
            name="breakDuration"
            control={control}
            render={({ field: controlField, fieldState }) => (
              <TimePicker
                {...controlField}
                label={'Break Duration'}
                onAccept={controlField.onChange}
                format={'HH:mm'}
                views={['hours', 'minutes']}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    error: !!fieldState?.error,
                    helperText: fieldState?.error?.message,
                  },
                }}
              />
            )}
          />
          <StyledFormActions>
            <Button text disabled={isLoading} onClick={onClose}>Cancel</Button>
            <Button primary disabled={isLoading} type="submit">Update</Button>
          </StyledFormActions>
        </StyledForm>
      </StyledContent>
    </Modal>
  )
}

const StyledContent = styled('div')`
  ${(props) => props.theme.breakpoints.up('sm')} {
    min-width: ${(props) => props.theme.typography.pxToRem(540)};
  }
`

const StyledFormActions = styled('div')`
  position: sticky;
  bottom: 0;
  left: 0;
  right: 0;
  width: 100%;
  display: flex;
  justify-content: flex-end;
  gap: ${(props) => props.theme.typography.pxToRem(12)};
  margin-top: ${(props) => props.theme.typography.pxToRem(16)};
`
